import { Helmet } from "react-helmet-async";
import { useState } from "react";
// @mui
import {
  Card,
  Table,
  Stack,
  Paper,
  Checkbox,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  IconButton,
  TableContainer,
  TablePagination,
} from "@mui/material";
// utils
import {
  getComparator,
  applySortFilter,
} from "../../utils/sortFilterComparator";
import { fDateBR } from "../../utils/formatTime";
// hooks
import useFinancial from "../../hooks/Services/useFinancial";
// components
import SkeletonComponent from "../../components/skeleton";
import Iconify from "../../components/iconify";
import Scrollbar from "../../components/scrollbar";
// sections
import {
  UserListHead,
  UserListToolbar,
} from "../../sections/@dashboard/user/index";

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: "plate", label: "Placa", alignRight: false },
  { id: "Store.name", label: "Loja", alignRight: false },
  { id: "Store.responsiblePerName", label: "Vistoriador", alignRight: false },
  { id: "createdAt", label: "Data", alignRight: false },
];

// ----------------------------------------------------------------------

export default function ReportFinancial() {
  const { result, params, isLoading, setParams, searchFinancial } =
    useFinancial();

  const [page, setPage] = useState(0);

  const [order, setOrder] = useState("desc");

  const [selected, setSelected] = useState([]);

  const [orderBy, setOrderBy] = useState("createdAt");

  const [filterMesValue, setFilterMesValue] = useState({
    label: "",
    value: "",
  });

  const [filterPlacaValue, setFilterPlacaValue] = useState("");

  const [rowsPerPage, setRowsPerPage] = useState(20);

  const filteredRows = applySortFilter(
    result !== undefined ? result.data : [],
    getComparator(order, orderBy)
  );

  const isNotFound = !filteredRows.length && !isLoading;

  const searchFields = [
    {
      title: "Mes",
      apiField: "createdAtMonth",
      type: "date",
      onChange: (value) => setFilterMesValue(value),
      inputValue: filterMesValue,
    },
    {
      title: "Placa",
      apiField: "plate",
      type: "text",
      onChange: (e) => setFilterPlacaValue(e.target.value),
      inputValue: filterPlacaValue,
    },
  ];

  const handleRequestSort = (event, property) => {
    const isDesc = orderBy === property && order === "desc";

    if (isDesc && orderBy === property) {
      setOrderBy("id");
      setOrder("desc");
    } else if (orderBy === property) {
      setOrder("desc");
    } else {
      setOrderBy(property);
      setOrder("asc");
    }
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = result.data.map((n) => n.plate);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setParams({ ...params, page: newPage });
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setParams({ ...params, rowsPerPage: parseInt(event.target.value, 10) });
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const resetFilters = () => {
    setPage(0);
    setFilterMesValue({ label: "", value: "" });
    setFilterPlacaValue("");
    setParams({ ...params, page: 0 });
  };

  return (
    <>
      <Helmet>
        <title>Relatório - Financeiro</title>
      </Helmet>

      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            Relatório - Financeiro
          </Typography>
        </Stack>

        <Card>
          <UserListToolbar
            numSelected={selected.length}
            searchFunction={searchFinancial}
            resetFilters={resetFilters}
            searchFields={searchFields}
          />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <UserListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={result !== undefined ? result.data.length : 0}
                  numSelected={selected.length}
                  onRequestSort={handleRequestSort}
                  onSelectAllClick={handleSelectAllClick}
                />
                <TableBody>
                  {result !== undefined && result.data.length !== 0 ? (
                    filteredRows.map((row) => {
                      const { id, plate, Store, createdAt } = row;
                      // const selectedUser = selected.indexOf(plate) !== -1;}

                      return (
                        <TableRow hover key={id} tabIndex={-1} role="checkbox">
                          {/* selected={selectedUser}  <TableCell padding="checkbox">
                          <Checkbox checked={selectedUser} onChange={(event) => handleClick(event, plate)} />
                        </TableCell> */}

                          <TableCell align="left">
                            <Typography variant="subtitle2" noWrap>
                              {plate}
                            </Typography>
                          </TableCell>

                          <TableCell align="left">
                            {Store ? Store.name : "Nenhuma vinculada"}
                          </TableCell>

                          <TableCell align="left">
                            {Store?.responsiblePerName || ""}
                          </TableCell>

                          <TableCell align="left">
                            {fDateBR(createdAt)}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : isNotFound &&
                    (filterMesValue.value !== "" || filterPlacaValue !== "") ? (
                    <TableRow>
                      <TableCell align="center" colSpan={8} sx={{ py: 3 }}>
                        <Paper sx={{ textAlign: "center" }}>
                          <Typography variant="h6" paragraph>
                            Não encontrado
                          </Typography>
                          <Typography variant="body2">
                            Nenhum resultado para &nbsp;
                            <strong>
                              &quot;
                              {`Mes: ${filterMesValue.label} Placa: ${filterPlacaValue}`}
                              &quot;
                            </strong>
                            .
                            <br /> Tente verificar se há erros de digitação ou
                            usar palavras completas.
                          </Typography>
                        </Paper>
                      </TableCell>
                    </TableRow>
                  ) : isNotFound &&
                    filterMesValue.value === "" &&
                    filterPlacaValue === "" ? (
                    <TableRow>
                      <TableCell align="center" colSpan={8} sx={{ py: 3 }}>
                        <Paper sx={{ textAlign: "center" }}>
                          <Typography variant="h6" paragraph>
                            Não há registros
                          </Typography>
                        </Paper>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <>
                      <SkeletonComponent numberOfRows={4} />
                      <SkeletonComponent numberOfRows={4} />
                      <SkeletonComponent numberOfRows={4} />
                      <SkeletonComponent numberOfRows={4} />
                      <SkeletonComponent numberOfRows={4} />
                    </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            count={result && result.total !== undefined ? result.total : 0}
            onPageChange={handleChangePage}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[20, 50, 100]}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Resultado por página:"
            component="div"
            sx={{
              "& .MuiTablePagination-selectLabel": {
                marginTop: "15px",
              },
              "& .MuiTablePagination-displayedRows": {
                marginTop: "15px",
              },
            }}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count !== -1 ? count : to}`
            }
          />
        </Card>
      </Container>
    </>
  );
}
